import React, { useState, useEffect, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { AtAGlanceReport } from '../../types';
import { StorageService } from '../../lib/storage';
import { getLocalDateString, getLocalMonthString } from '../../lib/dateUtils';
import {
  Printer,
  Save,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  Sparkles,
  Zap,
} from 'lucide-react';

export const AtAGlanceView: React.FC = () => {
  const { user, personalReports, triggerConfetti } = useApp();

  const defaultMonth = getLocalMonthString();

  const [selectedMonth, setSelectedMonth] = useState<string>(defaultMonth);
  const [isSavedAlert, setIsSavedAlert] = useState(false);
  const [isAutoSyncAlert, setIsAutoSyncAlert] = useState(false);

  // Form State
  const [formData, setFormData] = useState<AtAGlanceReport>({
    month: defaultMonth,
    userName: user?.name || '',
  });

  // Calculate live daily entries for the selected month
  const monthEntries = useMemo(() => {
    return personalReports.filter((r) => r.date.startsWith(selectedMonth));
  }, [personalReports, selectedMonth]);

  // Compute live summary aggregates directly from 31-day personal report table
  const computedSummary = useMemo(() => {
    let quranDays = 0, quranVerses = 0;
    let hadithDays = 0, hadithCount = 0;
    let litTotal = 0, litIslamic = 0, litOthers = 0;
    let academicDays = 0, academicHours = 0, classTotal = 0, classAttended = 0;
    let salahJamat = 0, salahQaza = 0;
    let cMember = 0, cAssociate = 0, cWorker = 0, cSupporter = 0, cFriend = 0;
    let cTalented = 0, cWellWisher = 0;
    let distLit = 0, distMag = 0, distCard = 0, distGift = 0;
    let dawahHrs = 0, otherOrgHrs = 0, dawahDays = 0, otherOrgDays = 0;
    let exerciseDays = 0, newspaperDays = 0, selfAccDays = 0;

    monthEntries.forEach((e) => {
      const v = (e.quranTilawat || 0) + (e.quranTafseer || 0);
      if (v > 0) { quranDays++; quranVerses += v; }

      const h = e.hadithCount || 0;
      if (h > 0) { hadithDays++; hadithCount += h; }

      const lg = e.literatureGeneral || 0;
      const ls = e.literatureSyllabus || 0;
      if (lg > 0 || ls > 0) {
        litTotal += (lg + ls);
        litIslamic += ls;
        litOthers += lg;
      }

      const st = e.academicStudyHours || 0;
      if (st > 0) { academicDays++; academicHours += st; }

      classTotal += (e.classCount || 0);
      classAttended += (e.classAttended || 0);

      salahJamat += (e.salahJamat || 0);
      salahQaza += (e.salahQaza || 0);

      cMember += (e.contactMember || 0);
      cAssociate += (e.contactAssociate || 0);
      cWorker += (e.contactWorker || 0);
      cSupporter += (e.contactSupporter || 0);
      cFriend += (e.contactFriend || 0);
      cTalented += (e.contactTalented || 0);
      cWellWisher += (e.contactWellWisher || 0);

      distLit += (e.distLiterature || 0);
      distMag += (e.distMagazine || 0);
      distCard += (e.distStickerCard || 0);
      distGift += (e.distGift || 0);

      const dh = e.dutyDawahHours || 0;
      if (dh > 0) { dawahDays++; dawahHrs += dh; }

      const oh = e.dutyOtherHours || 0;
      if (oh > 0) { otherOrgDays++; otherOrgHrs += oh; }

      if (e.exerciseDone) exerciseDays++;
      if (e.newspaperRead) newspaperDays++;
      if (e.selfAccountability) selfAccDays++;
    });

    const daysCount = monthEntries.length || 1;
    const avgQuran = quranDays > 0 ? (quranVerses / quranDays).toFixed(1) : '0';
    const avgHadith = hadithDays > 0 ? (hadithCount / hadithDays).toFixed(1) : '0';
    const avgAcademic = academicDays > 0 ? (academicHours / academicDays).toFixed(1) : '0.0';
    const avgJamat = (salahJamat / daysCount).toFixed(1);
    const avgDawah = dawahDays > 0 ? (dawahHrs / dawahDays).toFixed(1) : '0.0';
    const avgOtherOrg = otherOrgDays > 0 ? (otherOrgHrs / otherOrgDays).toFixed(1) : '0.0';

    return {
      quranTotalDays: quranDays,
      quranAvgVerses: avgQuran,
      hadithTotalDays: hadithDays,
      hadithAvgCount: avgHadith,
      literatureTotalPages: litTotal,
      literatureIslamic: litIslamic,
      literatureOthers: litOthers,
      academicTotalDays: academicDays,
      academicAvgHours: avgAcademic,
      academicTotalClasses: classTotal,
      academicTotalAttendance: classAttended,
      salahJamatTotal: salahJamat,
      salahJamatAvg: avgJamat,
      salahQazaTotal: salahQaza,
      contactMember: cMember,
      contactAssociate: cAssociate,
      contactWorker: cWorker,
      contactSupporter: cSupporter,
      contactFriend: cFriend,
      contactMeritoriousStudent: cTalented,
      contactWellWisher: cWellWisher,
      dawahDays: dawahDays,
      dawahHours: avgDawah,
      orgOtherDays: otherOrgDays,
      orgOtherAvgHours: avgOtherOrg,
      distIslamicLiterature: distLit,
      distKishorPatrika: distMag,
      distStickerCard: distCard,
      distGift: distGift,
      miscPhysicalExerciseDays: exerciseDays,
      miscNewspaperDays: newspaperDays,
      miscSelfEvaluationDays: selfAccDays,
    };
  }, [monthEntries]);

  // Load saved report and auto-sync computed summary live
  useEffect(() => {
    const loaded = StorageService.getAtAGlanceReport(selectedMonth);

    if (monthEntries.length > 0) {
      setFormData((prev) => ({
        ...loaded,
        month: selectedMonth,
        userName: loaded.userName || user?.name || '',
        // Automatically sync calculated metrics live from Personal Report
        quranTotalDays: computedSummary.quranTotalDays,
        quranAvgVerses: computedSummary.quranAvgVerses,
        hadithTotalDays: computedSummary.hadithTotalDays,
        hadithAvgCount: computedSummary.hadithAvgCount,
        literatureTotalPages: computedSummary.literatureTotalPages,
        literatureIslamic: computedSummary.literatureIslamic,
        literatureOthers: computedSummary.literatureOthers,
        academicTotalDays: computedSummary.academicTotalDays,
        academicAvgHours: computedSummary.academicAvgHours,
        academicTotalClasses: computedSummary.academicTotalClasses,
        academicTotalAttendance: computedSummary.academicTotalAttendance,
        salahJamatTotal: computedSummary.salahJamatTotal,
        salahJamatAvg: computedSummary.salahJamatAvg,
        salahQazaTotal: computedSummary.salahQazaTotal,
        contactMember: computedSummary.contactMember,
        contactAssociate: computedSummary.contactAssociate,
        contactWorker: computedSummary.contactWorker,
        contactSupporter: computedSummary.contactSupporter,
        contactFriend: computedSummary.contactFriend,
        contactMeritoriousStudent: computedSummary.contactMeritoriousStudent,
        contactWellWisher: computedSummary.contactWellWisher,
        dawahDays: computedSummary.dawahDays,
        dawahHours: computedSummary.dawahHours,
        orgOtherDays: computedSummary.orgOtherDays,
        orgOtherAvgHours: computedSummary.orgOtherAvgHours,
        distIslamicLiterature: computedSummary.distIslamicLiterature,
        distKishorPatrika: computedSummary.distKishorPatrika,
        distStickerCard: computedSummary.distStickerCard,
        distGift: computedSummary.distGift,
        miscPhysicalExerciseDays: computedSummary.miscPhysicalExerciseDays,
        miscNewspaperDays: computedSummary.miscNewspaperDays,
        miscSelfEvaluationDays: computedSummary.miscSelfEvaluationDays,
      }));
    } else {
      setFormData({
        ...loaded,
        month: selectedMonth,
        userName: loaded.userName || user?.name || '',
      });
    }
  }, [selectedMonth, personalReports, user?.name, computedSummary, monthEntries.length]);

  const handleChange = (field: keyof AtAGlanceReport, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = () => {
    StorageService.saveAtAGlanceReport({
      ...formData,
      month: selectedMonth,
    });
    setIsSavedAlert(true);
    triggerConfetti();
    setTimeout(() => setIsSavedAlert(false), 3500);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset this summary report to blank?')) {
      const blank: AtAGlanceReport = {
        month: selectedMonth,
        userName: user?.name || '',
      };
      setFormData(blank);
      StorageService.saveAtAGlanceReport(blank);
    }
  };

  // Force re-calculate and notify
  const handleForceRecalculate = () => {
    if (monthEntries.length === 0) {
      alert(`No daily entries found in the Personal Report for ${selectedMonth}. Fill daily reports first in Personal Report sheet.`);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      ...computedSummary,
    }));

    setIsAutoSyncAlert(true);
    triggerConfetti();
    setTimeout(() => setIsAutoSyncAlert(false), 4000);
  };

  // Month navigation helpers
  const handlePrevMonth = () => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const prevDate = new Date(year, month - 2, 1);
    const prevMonthStr = `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}`;
    setSelectedMonth(prevMonthStr);
  };

  const handleNextMonth = () => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const nextDate = new Date(year, month - 1, 1);
    const nextMonthStr = `${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, '0')}`;
    setSelectedMonth(nextMonthStr);
  };

  return (
    <div className="space-y-6 animate-fade-in pb-16 print:p-0 print:m-0 print:space-y-0 print:pb-0">
      {/* Top Action Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 p-3.5 sm:p-4 bg-white dark:bg-[#111827] border border-slate-200/90 dark:border-slate-800 rounded-2xl sm:rounded-3xl shadow-xs print:hidden">
        <div className="flex items-center gap-2.5 sm:gap-3">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-2xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold shrink-0">
            <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
          </div>
          <div>
            <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
              <h2 className="text-base sm:text-lg font-black text-slate-900 dark:text-white tracking-tight">
                Monthly Report At a Glance
              </h2>
              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                {selectedMonth}
              </span>
              {monthEntries.length > 0 && (
                <span className="flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Auto-synced ({monthEntries.length} days)
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center flex-wrap gap-2 w-full sm:w-auto justify-between sm:justify-end">
          {/* Month Switcher */}
          <div className="flex items-center bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700/80 rounded-xl sm:rounded-2xl p-0.5 sm:p-1 shadow-xs flex-1 sm:flex-initial justify-between">
            <button
              onClick={handlePrevMonth}
              className="p-1.5 rounded-lg sm:rounded-xl hover:bg-white dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 transition-colors"
              title="Previous Month"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="px-1.5 sm:px-2 py-1 text-xs font-bold bg-transparent text-slate-800 dark:text-slate-200 focus:outline-hidden text-center min-w-0"
            />
            <button
              onClick={handleNextMonth}
              className="p-1.5 rounded-lg sm:rounded-xl hover:bg-white dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 transition-colors"
              title="Next Month"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Force Re-calculate Button */}
          <button
            onClick={handleForceRecalculate}
            className="flex-1 sm:flex-initial px-3 py-2 sm:py-2.5 bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-300 dark:border-emerald-800 hover:bg-emerald-100 text-emerald-700 dark:text-emerald-300 rounded-xl sm:rounded-2xl text-xs font-bold shadow-xs flex items-center justify-center gap-1.5 transition-all whitespace-nowrap"
            title="Recalculate all sums and averages from Personal Report sheet"
          >
            <Zap className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
            <span>Recalculate</span>
          </button>

          {/* Reset */}
          <button
            onClick={handleReset}
            className="p-2 sm:p-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700/80 hover:bg-rose-50 dark:hover:bg-rose-950/40 text-slate-400 hover:text-rose-500 rounded-xl sm:rounded-2xl text-xs font-bold transition-all shrink-0"
            title="Reset to blank"
          >
            <RotateCcw className="w-4 h-4" />
          </button>

          {/* Print 1-Page */}
          <button
            onClick={handlePrint}
            className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 sm:py-2.5 bg-sky-600 hover:bg-sky-500 text-white rounded-xl sm:rounded-2xl text-xs font-bold shadow-md shadow-sky-500/20 flex items-center justify-center gap-1.5 transition-all whitespace-nowrap"
            title="Print exactly 1 single A4 page"
          >
            <Printer className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
            <span>Print 1-Page</span>
          </button>

          {/* Save Button */}
          <button
            onClick={handleSave}
            className="flex-1 sm:flex-initial px-4 sm:px-5 py-2 sm:py-2.5 bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-500 hover:to-teal-400 text-white rounded-xl sm:rounded-2xl text-xs font-bold shadow-md shadow-emerald-500/25 flex items-center justify-center gap-1.5 transition-all transform active:scale-95 whitespace-nowrap"
          >
            <Save className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
            <span>Save Report</span>
          </button>
        </div>
      </div>

      {/* Feedback Alerts */}
      {isSavedAlert && (
        <div className="flex items-center gap-2 p-3.5 px-5 bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-300 dark:border-emerald-800 rounded-2xl text-emerald-800 dark:text-emerald-200 text-xs font-bold shadow-xs animate-in fade-in slide-in-from-top-2 print:hidden">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          <span>At a Glance report for {selectedMonth} saved successfully!</span>
        </div>
      )}

      {isAutoSyncAlert && (
        <div className="flex items-center gap-2 p-3.5 px-5 bg-blue-50 dark:bg-blue-950/50 border border-blue-300 dark:border-blue-800 rounded-2xl text-blue-800 dark:text-blue-200 text-xs font-bold shadow-xs animate-in fade-in slide-in-from-top-2 print:hidden">
          <Zap className="w-4 h-4 text-blue-600" />
          <span>Calculated totals and averages from the {monthEntries.length} daily entries in Personal Report!</span>
        </div>
      )}

      {/* Main Printable Sheet Container - Precision 1-Page Fit */}
      <div className="print-page-fit bg-white dark:bg-[#111827] text-slate-900 dark:text-slate-100 border border-slate-300 dark:border-slate-800 rounded-2xl sm:rounded-3xl p-3.5 sm:p-6 md:p-8 shadow-sm print:shadow-none print:border-none print:p-0 print:m-0 print:rounded-none font-sans text-xs print:text-[8px] print:leading-tight">
        {/* Document Header */}
        <div className="text-center mb-3.5 sm:mb-4 print:mb-1 space-y-0.5 print:space-y-0">
          <div className="text-xs print:text-[8.5px] font-semibold tracking-wide text-slate-600 dark:text-slate-400 font-serif">
            بِسْمِ اللَّهِ الرَّحْمَٰনِ الرَّحِيمِ
          </div>
          <h1 className="text-base sm:text-xl print:text-xs font-black tracking-tight text-slate-900 dark:text-white uppercase">
            Monthly Report At a Glance
          </h1>
        </div>

        {/* User Info & Month Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 print:flex-row print:gap-2 pb-3 print:pb-1 border-b border-slate-200 dark:border-slate-800 mb-3.5 sm:mb-4 print:mb-1.5 text-xs print:text-[8px]">
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <span className="font-bold text-slate-700 dark:text-slate-300 whitespace-nowrap text-xs">Name:</span>
            <input
              type="text"
              placeholder="Your Name"
              value={formData.userName || ''}
              onChange={(e) => handleChange('userName', e.target.value)}
              className="flex-1 sm:w-48 print:w-40 min-w-0 px-2 py-0.5 bg-slate-50 dark:bg-slate-900 border-b border-slate-300 dark:border-slate-700 font-semibold focus:outline-hidden"
            />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <span className="font-bold text-slate-700 dark:text-slate-300 whitespace-nowrap text-xs">Month:</span>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="flex-1 sm:w-auto min-w-0 px-2 py-0.5 bg-slate-50 dark:bg-slate-900 border-b border-slate-300 dark:border-slate-700 font-bold focus:outline-hidden"
            />
          </div>
        </div>

        {/* SECTION 1: 2-Column Grid for Primary Practices */}
        <div className="grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-3 print:gap-1">
          {/* 1. Quran Study */}
          <div className="border border-slate-300 dark:border-slate-700 rounded-xl p-3 print:p-1.5 bg-slate-50/50 dark:bg-slate-900/30 print:rounded-md">
            <div className="flex items-center justify-between pb-1.5 print:pb-0.5 border-b border-slate-200 dark:border-slate-800">
              <h3 className="font-bold text-slate-900 dark:text-white uppercase tracking-wider text-[10.5px] print:text-[8.5px]">
                Quran Study
              </h3>
              {monthEntries.length > 0 && (
                <span className="text-[9px] print:text-[7px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-1 py-0.2 rounded-sm print:hidden">
                  Synced
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 print:gap-1 pt-2 print:pt-0.5">
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0 print:hidden" />
                <span className="text-slate-600 dark:text-slate-400 font-medium">Total Days:</span>
                <input
                  type="text"
                  placeholder="0"
                  value={formData.quranTotalDays ?? ''}
                  onChange={(e) => handleChange('quranTotalDays', e.target.value)}
                  className="w-14 print:w-10 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden text-emerald-700 dark:text-emerald-300"
                />
              </div>

              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0 print:hidden" />
                <span className="text-slate-600 dark:text-slate-400 font-medium">Avg. Verses:</span>
                <input
                  type="text"
                  placeholder="0"
                  value={formData.quranAvgVerses ?? ''}
                  onChange={(e) => handleChange('quranAvgVerses', e.target.value)}
                  className="w-14 print:w-10 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden text-emerald-700 dark:text-emerald-300"
                />
              </div>

              <div className="flex items-center gap-1.5 col-span-2">
                <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Surah Name:</span>
                <input
                  type="text"
                  placeholder="Surah name..."
                  value={formData.quranSurahName || ''}
                  onChange={(e) => handleChange('quranSurahName', e.target.value)}
                  className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-medium focus:outline-hidden"
                />
              </div>

              <div className="flex items-center gap-1.5 col-span-2">
                <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Dars Prepared:</span>
                <input
                  type="text"
                  placeholder="Dars topic..."
                  value={formData.quranDarsPrepared || ''}
                  onChange={(e) => handleChange('quranDarsPrepared', e.target.value)}
                  className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-medium focus:outline-hidden"
                />
              </div>

              <div className="flex items-center gap-1.5 col-span-2">
                <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Memorized (Ayat):</span>
                <input
                  type="text"
                  placeholder="0"
                  value={formData.quranMemorizeVerses || ''}
                  onChange={(e) => handleChange('quranMemorizeVerses', e.target.value)}
                  className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-medium focus:outline-hidden"
                />
              </div>

              <div className="flex items-center gap-1.5 col-span-2">
                <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Meaning Study:</span>
                <input
                  type="text"
                  placeholder="Surah with Tafseer..."
                  value={formData.quranSurahWithMeaning || ''}
                  onChange={(e) => handleChange('quranSurahWithMeaning', e.target.value)}
                  className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-medium focus:outline-hidden"
                />
              </div>
            </div>
          </div>

          {/* 2. Hadith Study */}
          <div className="border border-slate-300 dark:border-slate-700 rounded-xl p-3 print:p-1.5 bg-slate-50/50 dark:bg-slate-900/30 print:rounded-md">
            <div className="flex items-center justify-between pb-1.5 print:pb-0.5 border-b border-slate-200 dark:border-slate-800">
              <h3 className="font-bold text-slate-900 dark:text-white uppercase tracking-wider text-[10.5px] print:text-[8.5px]">
                Hadith Study
              </h3>
              {monthEntries.length > 0 && (
                <span className="text-[9px] print:text-[7px] font-bold text-purple-600 dark:text-purple-400 bg-purple-500/10 px-1 py-0.2 rounded-sm print:hidden">
                  Synced
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 print:gap-1 pt-2 print:pt-0.5">
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0 print:hidden" />
                <span className="text-slate-600 dark:text-slate-400 font-medium">Total Days:</span>
                <input
                  type="text"
                  placeholder="0"
                  value={formData.hadithTotalDays ?? ''}
                  onChange={(e) => handleChange('hadithTotalDays', e.target.value)}
                  className="w-14 print:w-10 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden text-purple-700 dark:text-purple-300"
                />
              </div>

              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0 print:hidden" />
                <span className="text-slate-600 dark:text-slate-400 font-medium">Avg. Hadith:</span>
                <input
                  type="text"
                  placeholder="0"
                  value={formData.hadithAvgCount ?? ''}
                  onChange={(e) => handleChange('hadithAvgCount', e.target.value)}
                  className="w-14 print:w-10 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden text-purple-700 dark:text-purple-300"
                />
              </div>

              <div className="flex items-center gap-1.5 col-span-2">
                <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Book / Subject:</span>
                <input
                  type="text"
                  placeholder="Book / Topic..."
                  value={formData.hadithBookTopic || ''}
                  onChange={(e) => handleChange('hadithBookTopic', e.target.value)}
                  className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-medium focus:outline-hidden"
                />
              </div>

              <div className="flex items-center gap-1.5 col-span-2">
                <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Dars:</span>
                <input
                  type="text"
                  placeholder="Dars topic/no..."
                  value={formData.hadithDars || ''}
                  onChange={(e) => handleChange('hadithDars', e.target.value)}
                  className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-medium focus:outline-hidden"
                />
              </div>

              <div className="flex items-center gap-1.5 col-span-2">
                <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Memorized:</span>
                <input
                  type="text"
                  placeholder="0"
                  value={formData.hadithMemorized || ''}
                  onChange={(e) => handleChange('hadithMemorized', e.target.value)}
                  className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-medium focus:outline-hidden"
                />
              </div>

              <div className="flex items-center gap-1.5 col-span-2">
                <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Selected Topic:</span>
                <input
                  type="text"
                  placeholder="Topic of study..."
                  value={formData.hadithTopic || ''}
                  onChange={(e) => handleChange('hadithTopic', e.target.value)}
                  className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-medium focus:outline-hidden"
                />
              </div>
            </div>
          </div>

          {/* 3. Literature Study */}
          <div className="border border-slate-300 dark:border-slate-700 rounded-xl p-3 print:p-1.5 bg-slate-50/50 dark:bg-slate-900/30 print:rounded-md">
            <div className="flex items-center justify-between pb-1.5 print:pb-0.5 border-b border-slate-200 dark:border-slate-800">
              <h3 className="font-bold text-slate-900 dark:text-white uppercase tracking-wider text-[10.5px] print:text-[8.5px]">
                Literature Study
              </h3>
              {monthEntries.length > 0 && (
                <span className="text-[9px] print:text-[7px] font-bold text-amber-600 dark:text-amber-400 bg-amber-500/10 px-1 py-0.2 rounded-sm print:hidden">
                  Synced
                </span>
              )}
            </div>
            <div className="space-y-2 print:space-y-0.5 pt-2 print:pt-0.5">
              <div className="grid grid-cols-3 gap-2 print:gap-1">
                <div className="flex items-center gap-1">
                  <span className="text-slate-600 dark:text-slate-400 font-medium">Total:</span>
                  <input
                    type="text"
                    placeholder="0"
                    value={formData.literatureTotalPages ?? ''}
                    onChange={(e) => handleChange('literatureTotalPages', e.target.value)}
                    className="w-12 print:w-8 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden text-amber-700 dark:text-amber-300"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-slate-600 dark:text-slate-400 font-medium">Islamic:</span>
                  <input
                    type="text"
                    placeholder="0"
                    value={formData.literatureIslamic ?? ''}
                    onChange={(e) => handleChange('literatureIslamic', e.target.value)}
                    className="w-12 print:w-8 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden text-amber-700 dark:text-amber-300"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-slate-600 dark:text-slate-400 font-medium">Others:</span>
                  <input
                    type="text"
                    placeholder="0"
                    value={formData.literatureOthers ?? ''}
                    onChange={(e) => handleChange('literatureOthers', e.target.value)}
                    className="w-12 print:w-8 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden text-amber-700 dark:text-amber-300"
                  />
                </div>
              </div>

              <div className="flex items-center gap-1.5">
                <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Book Names:</span>
                <input
                  type="text"
                  placeholder="Books read this month..."
                  value={formData.literatureBookNames || ''}
                  onChange={(e) => handleChange('literatureBookNames', e.target.value)}
                  className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-medium focus:outline-hidden"
                />
              </div>

              <div className="grid grid-cols-2 gap-2 print:gap-1">
                <div className="flex items-center gap-1">
                  <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Note:</span>
                  <input
                    type="text"
                    placeholder="Notes taken..."
                    value={formData.literatureBookNotes || ''}
                    onChange={(e) => handleChange('literatureBookNotes', e.target.value)}
                    className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-medium focus:outline-hidden"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Disc:</span>
                  <input
                    type="text"
                    placeholder="Discussion notes..."
                    value={formData.literatureDiscussionNotes || ''}
                    onChange={(e) => handleChange('literatureDiscussionNotes', e.target.value)}
                    className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-medium focus:outline-hidden"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 4. Academic Study & Salah */}
          <div className="border border-slate-300 dark:border-slate-700 rounded-xl p-3 print:p-1.5 bg-slate-50/50 dark:bg-slate-900/30 print:rounded-md">
            <div className="flex items-center justify-between pb-1.5 print:pb-0.5 border-b border-slate-200 dark:border-slate-800">
              <h3 className="font-bold text-slate-900 dark:text-white uppercase tracking-wider text-[10.5px] print:text-[8.5px]">
                Academic Study & Salah
              </h3>
              {monthEntries.length > 0 && (
                <span className="text-[9px] print:text-[7px] font-bold text-blue-600 dark:text-blue-400 bg-blue-500/10 px-1 py-0.2 rounded-sm print:hidden">
                  Synced
                </span>
              )}
            </div>
            <div className="space-y-2 print:space-y-0.5 pt-2 print:pt-0.5">
              <div className="grid grid-cols-4 gap-1.5 print:gap-1">
                <div className="flex items-center gap-1">
                  <span className="text-slate-600 dark:text-slate-400 font-medium">Days:</span>
                  <input
                    type="text"
                    placeholder="0"
                    value={formData.academicTotalDays ?? ''}
                    onChange={(e) => handleChange('academicTotalDays', e.target.value)}
                    className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden text-blue-700 dark:text-blue-300"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-slate-600 dark:text-slate-400 font-medium">Hours:</span>
                  <input
                    type="text"
                    placeholder="0.0"
                    value={formData.academicAvgHours ?? ''}
                    onChange={(e) => handleChange('academicAvgHours', e.target.value)}
                    className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden text-blue-700 dark:text-blue-300"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-slate-600 dark:text-slate-400 font-medium">Class:</span>
                  <input
                    type="text"
                    placeholder="0"
                    value={formData.academicTotalClasses ?? ''}
                    onChange={(e) => handleChange('academicTotalClasses', e.target.value)}
                    className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden text-blue-700 dark:text-blue-300"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-slate-600 dark:text-slate-400 font-medium">Attnd:</span>
                  <input
                    type="text"
                    placeholder="0"
                    value={formData.academicTotalAttendance ?? ''}
                    onChange={(e) => handleChange('academicTotalAttendance', e.target.value)}
                    className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden text-blue-700 dark:text-blue-300"
                  />
                </div>
              </div>

              {/* Salah Quantitative Inputs */}
              <div className="pt-1.5 print:pt-0.5 border-t border-slate-200 dark:border-slate-800">
                <div className="grid grid-cols-3 gap-2 print:gap-1">
                  <div className="flex items-center gap-1">
                    <span className="text-slate-600 dark:text-slate-400 font-medium">Jamat:</span>
                    <input
                      type="text"
                      placeholder="0"
                      value={formData.salahJamatTotal ?? ''}
                      onChange={(e) => handleChange('salahJamatTotal', e.target.value)}
                      className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden text-emerald-700 dark:text-emerald-300"
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-slate-600 dark:text-slate-400 font-medium">Avg:</span>
                    <input
                      type="text"
                      placeholder="0.0"
                      value={formData.salahJamatAvg ?? ''}
                      onChange={(e) => handleChange('salahJamatAvg', e.target.value)}
                      className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden text-emerald-700 dark:text-emerald-300"
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-slate-600 dark:text-slate-400 font-medium">Qaza:</span>
                    <input
                      type="text"
                      placeholder="0"
                      value={formData.salahQazaTotal ?? ''}
                      onChange={(e) => handleChange('salahQazaTotal', e.target.value)}
                      className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden text-rose-600 dark:text-rose-400"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 5. Contacts & Outreach */}
          <div className="border border-slate-300 dark:border-slate-700 rounded-xl p-3 print:p-1.5 bg-slate-50/50 dark:bg-slate-900/30 print:rounded-md">
            <div className="flex items-center justify-between pb-1.5 print:pb-0.5 border-b border-slate-200 dark:border-slate-800">
              <h3 className="font-bold text-slate-900 dark:text-white uppercase tracking-wider text-[10.5px] print:text-[8.5px]">
                Contacts & Outreach
              </h3>
              {monthEntries.length > 0 && (
                <span className="text-[9px] print:text-[7px] font-bold text-teal-600 dark:text-teal-400 bg-teal-500/10 px-1 py-0.2 rounded-sm print:hidden">
                  Synced
                </span>
              )}
            </div>
            <div className="grid grid-cols-3 print:grid-cols-4 gap-1.5 print:gap-0.5 pt-2 print:pt-0.5">
              {[
                { key: 'contactMember', label: 'Member' },
                { key: 'contactAssociate', label: 'Assoc' },
                { key: 'contactWorker', label: 'Worker' },
                { key: 'contactSupporter', label: 'Supprt' },
                { key: 'contactFriend', label: 'Friend' },
                { key: 'contactWellWisher', label: 'Wisher' },
                { key: 'contactSchoolStudent', label: 'Student' },
                { key: 'contactMeritoriousStudent', label: 'Talent' },
                { key: 'contactTeacher', label: 'Teacher' },
                { key: 'contactMuharrama', label: 'Muharr' },
                { key: 'contactVip', label: 'VIP' },
              ].map((item) => (
                <div key={item.key} className="flex items-center gap-1">
                  <span className="text-slate-600 dark:text-slate-400 font-medium truncate" title={item.label}>
                    {item.label}:
                  </span>
                  <input
                    type="text"
                    placeholder="0"
                    value={(formData as any)[item.key] ?? ''}
                    onChange={(e) => handleChange(item.key as keyof AtAGlanceReport, e.target.value)}
                    className="w-8 print:w-6 px-0.5 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden text-teal-700 dark:text-teal-300"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* 6. Organizational Duties */}
          <div className="border border-slate-300 dark:border-slate-700 rounded-xl p-3 print:p-1.5 bg-slate-50/50 dark:bg-slate-900/30 print:rounded-md">
            <div className="flex items-center justify-between pb-1.5 print:pb-0.5 border-b border-slate-200 dark:border-slate-800">
              <h3 className="font-bold text-slate-900 dark:text-white uppercase tracking-wider text-[10.5px] print:text-[8.5px]">
                Organizational Duties
              </h3>
              {monthEntries.length > 0 && (
                <span className="text-[9px] print:text-[7px] font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-500/10 px-1 py-0.2 rounded-sm print:hidden">
                  Synced
                </span>
              )}
            </div>
            <div className="space-y-2 print:space-y-0.5 pt-2 print:pt-0.5">
              <div className="grid grid-cols-3 gap-1.5 print:gap-1">
                <div className="flex items-center gap-1">
                  <span className="text-slate-600 dark:text-slate-400 font-medium">Dawah D:</span>
                  <input
                    type="text"
                    placeholder="0"
                    value={formData.dawahDays ?? ''}
                    onChange={(e) => handleChange('dawahDays', e.target.value)}
                    className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden text-indigo-700 dark:text-indigo-300"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-slate-600 dark:text-slate-400 font-medium">Dawah H:</span>
                  <input
                    type="text"
                    placeholder="0.0"
                    value={formData.dawahHours ?? ''}
                    onChange={(e) => handleChange('dawahHours', e.target.value)}
                    className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden text-indigo-700 dark:text-indigo-300"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-slate-600 dark:text-slate-400 font-medium">Persons:</span>
                  <input
                    type="text"
                    placeholder="0"
                    value={formData.dawahPersons || ''}
                    onChange={(e) => handleChange('dawahPersons', e.target.value)}
                    className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
                  />
                </div>
              </div>

              <div className="pt-1.5 print:pt-0.5 border-t border-slate-200 dark:border-slate-800 grid grid-cols-2 gap-2 print:gap-1">
                <div className="flex items-center gap-1">
                  <span className="text-slate-600 dark:text-slate-400 font-medium">Other D:</span>
                  <input
                    type="text"
                    placeholder="0"
                    value={formData.orgOtherDays ?? ''}
                    onChange={(e) => handleChange('orgOtherDays', e.target.value)}
                    className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden text-indigo-700 dark:text-indigo-300"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-slate-600 dark:text-slate-400 font-medium">Other H:</span>
                  <input
                    type="text"
                    placeholder="0.0"
                    value={formData.orgOtherAvgHours ?? ''}
                    onChange={(e) => handleChange('orgOtherAvgHours', e.target.value)}
                    className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden text-indigo-700 dark:text-indigo-300"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 2: Material Distribution */}
        <div className="mt-3 print:mt-1 border border-slate-300 dark:border-slate-700 rounded-xl p-3 print:p-1.5 bg-slate-50/50 dark:bg-slate-900/30 print:rounded-md">
          <div className="flex items-center justify-between pb-1 print:pb-0.5 border-b border-slate-200 dark:border-slate-800">
            <h3 className="font-bold text-slate-900 dark:text-white uppercase tracking-wider text-[10.5px] print:text-[8.5px]">
              Material Distribution
            </h3>
            {monthEntries.length > 0 && (
              <span className="text-[9px] print:text-[7px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-1 py-0.2 rounded-sm print:hidden">
                Synced
              </span>
            )}
          </div>
          <div className="grid grid-cols-3 print:grid-cols-5 gap-x-4 print:gap-x-2 gap-y-1.5 print:gap-y-0.5 pt-2 print:pt-0.5">
            {[
              { key: 'distIslamicLiterature', label: 'Books' },
              { key: 'distChhatrasangbad', label: 'Magazine' },
              { key: 'distClassRoutine', label: 'Routine' },
              { key: 'distKishorPatrika', label: 'Youth Mag' },
              { key: 'distEnglishMagazine', label: 'Eng Mag' },
              { key: 'distStickerCard', label: 'Card' },
              { key: 'distYw', label: 'Y.W' },
              { key: 'distParichiti', label: 'Brochure' },
              { key: 'distGift', label: 'Gift' },
            ].map((item) => (
              <div key={item.key} className="flex items-center justify-between gap-1 border-b border-slate-200/80 dark:border-slate-800/80 pb-0.5">
                <span className="text-slate-700 dark:text-slate-300 font-medium truncate">
                  {item.label}:
                </span>
                <input
                  type="text"
                  placeholder="0"
                  value={(formData as any)[item.key] ?? ''}
                  onChange={(e) => handleChange(item.key as keyof AtAGlanceReport, e.target.value)}
                  className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-right font-bold focus:outline-hidden text-emerald-700 dark:text-emerald-300"
                />
              </div>
            ))}
          </div>
        </div>

        {/* SECTION 3: Growth */}
        <div className="mt-3 print:mt-1 border border-slate-300 dark:border-slate-700 rounded-xl p-3 print:p-1.5 bg-slate-50/50 dark:bg-slate-900/30 print:rounded-md">
          <h3 className="font-bold text-slate-900 dark:text-white uppercase tracking-wider text-[10.5px] print:text-[8.5px] pb-1 print:pb-0.5 border-b border-slate-200 dark:border-slate-800">
            Growth & Manpower Increase
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-4 print:gap-2 pt-2 print:pt-0.5">
            <div className="space-y-1.5 print:space-y-0.5">
              {[
                { countKey: 'growthMemberCount', descKey: 'growthMemberDesc', label: 'Member' },
                { countKey: 'growthMemberCandidateCount', descKey: 'growthMemberCandidateDesc', label: 'Member Cand.' },
                { countKey: 'growthAssociateCount', descKey: 'growthAssociateDesc', label: 'Associate' },
                { countKey: 'growthAssociateCandidateCount', descKey: 'growthAssociateCandidateDesc', label: 'Assoc Cand.' },
              ].map((item) => (
                <div key={item.countKey} className="flex items-center gap-1.5">
                  <span className="w-24 print:w-20 text-slate-700 dark:text-slate-300 font-medium whitespace-nowrap">
                    {item.label}:
                  </span>
                  <input
                    type="text"
                    placeholder="0"
                    value={(formData as any)[item.countKey] || ''}
                    onChange={(e) => handleChange(item.countKey as keyof AtAGlanceReport, e.target.value)}
                    className="w-8 print:w-6 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
                  />
                  <input
                    type="text"
                    placeholder="Names / remarks..."
                    value={(formData as any)[item.descKey] || ''}
                    onChange={(e) => handleChange(item.descKey as keyof AtAGlanceReport, e.target.value)}
                    className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-medium focus:outline-hidden truncate"
                  />
                </div>
              ))}
            </div>

            <div className="space-y-1.5 print:space-y-0.5">
              {[
                { countKey: 'growthWorkerCount', descKey: 'growthWorkerDesc', label: 'Worker' },
                { countKey: 'growthSupporterCount', descKey: 'growthSupporterDesc', label: 'Supporter' },
                { countKey: 'growthFriendCount', descKey: 'growthFriendDesc', label: 'Friend' },
                { countKey: 'growthWellWisherCount', descKey: 'growthWellWisherDesc', label: 'Well-wisher' },
              ].map((item) => (
                <div key={item.countKey} className="flex items-center gap-1.5">
                  <span className="w-24 print:w-20 text-slate-700 dark:text-slate-300 font-medium whitespace-nowrap">
                    {item.label}:
                  </span>
                  <input
                    type="text"
                    placeholder="0"
                    value={(formData as any)[item.countKey] || ''}
                    onChange={(e) => handleChange(item.countKey as keyof AtAGlanceReport, e.target.value)}
                    className="w-8 print:w-6 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
                  />
                  <input
                    type="text"
                    placeholder="Names / remarks..."
                    value={(formData as any)[item.descKey] || ''}
                    onChange={(e) => handleChange(item.descKey as keyof AtAGlanceReport, e.target.value)}
                    className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-medium focus:outline-hidden truncate"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* SECTION 4: Baitulmal */}
        <div className="mt-3 print:mt-1 border border-slate-300 dark:border-slate-700 rounded-xl p-3 print:p-1.5 bg-slate-50/50 dark:bg-slate-900/30 print:rounded-md">
          <h3 className="font-bold text-slate-900 dark:text-white uppercase tracking-wider text-[10.5px] print:text-[8.5px] pb-1 print:pb-0.5 border-b border-slate-200 dark:border-slate-800">
            Baitulmal
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 print:grid-cols-6 gap-x-3 gap-y-2 print:gap-1 pt-2 print:pt-0.5">
            <div className="flex items-center gap-1">
              <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Date:</span>
              <input
                type="date"
                value={formData.baitulmalPaymentDate || ''}
                onChange={(e) => handleChange('baitulmalPaymentDate', e.target.value)}
                className="flex-1 px-1 py-0.5 print:py-0 border-b border-slate-300 bg-transparent font-medium focus:outline-hidden"
              />
            </div>

            <div className="flex items-center gap-1">
              <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Personal:</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="0"
                value={formData.baitulmalPersonalIncrease || ''}
                onChange={(e) => handleChange('baitulmalPersonalIncrease', e.target.value)}
                className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-bold text-right focus:outline-hidden"
              />
              <span className="text-slate-500 font-bold">৳</span>
            </div>

            <div className="flex items-center gap-1">
              <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Total Inc:</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="0"
                value={formData.baitulmalTotalIncrease || ''}
                onChange={(e) => handleChange('baitulmalTotalIncrease', e.target.value)}
                className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-bold text-right focus:outline-hidden"
              />
              <span className="text-slate-500 font-bold">৳</span>
            </div>

            <div className="flex items-center gap-1">
              <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Welfare:</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="0"
                value={formData.baitulmalStudentWelfare || ''}
                onChange={(e) => handleChange('baitulmalStudentWelfare', e.target.value)}
                className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-bold text-right focus:outline-hidden"
              />
              <span className="text-slate-500 font-bold">৳</span>
            </div>

            <div className="flex items-center gap-1">
              <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Table Bank:</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="0"
                value={formData.baitulmalTableBank || ''}
                onChange={(e) => handleChange('baitulmalTableBank', e.target.value)}
                className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-bold text-right focus:outline-hidden"
              />
              <span className="text-slate-500 font-bold">৳</span>
            </div>

            <div className="flex items-center gap-1">
              <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Charity:</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="0"
                value={formData.baitulmalCharityBox || ''}
                onChange={(e) => handleChange('baitulmalCharityBox', e.target.value)}
                className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-bold text-right focus:outline-hidden"
              />
              <span className="text-slate-500 font-bold">৳</span>
            </div>
          </div>
        </div>

        {/* SECTION 5: Miscellaneous */}
        <div className="mt-3 print:mt-1 border border-slate-300 dark:border-slate-700 rounded-xl p-3 print:p-1.5 bg-slate-50/50 dark:bg-slate-900/30 print:rounded-md">
          <div className="flex items-center justify-between pb-1 print:pb-0.5 border-b border-slate-200 dark:border-slate-800">
            <h3 className="font-bold text-slate-900 dark:text-white uppercase tracking-wider text-[10.5px] print:text-[8.5px]">
              Miscellaneous & Self Development
            </h3>
            {monthEntries.length > 0 && (
              <span className="text-[9px] print:text-[7px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-1 py-0.2 rounded-sm print:hidden">
                Synced
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 print:grid-cols-6 gap-x-3 gap-y-2 print:gap-1 pt-2 print:pt-0.5">
            <div className="flex items-center gap-1">
              <span className="text-slate-600 dark:text-slate-400 font-medium">Self-Eval:</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="0"
                value={formData.miscSelfEvaluationDays ?? ''}
                onChange={(e) => handleChange('miscSelfEvaluationDays', e.target.value)}
                className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden text-emerald-700 dark:text-emerald-300"
              />
              <span className="text-slate-500 text-[9px] print:text-[7px]">Days</span>
            </div>

            <div className="flex items-center gap-1">
              <span className="text-slate-600 dark:text-slate-400 font-medium">Exercise:</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="0"
                value={formData.miscPhysicalExerciseDays ?? ''}
                onChange={(e) => handleChange('miscPhysicalExerciseDays', e.target.value)}
                className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden text-emerald-700 dark:text-emerald-300"
              />
              <span className="text-slate-500 text-[9px] print:text-[7px]">Days</span>
            </div>

            <div className="flex items-center gap-1">
              <span className="text-slate-600 dark:text-slate-400 font-medium">Paper:</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="0"
                value={formData.miscNewspaperDays ?? ''}
                onChange={(e) => handleChange('miscNewspaperDays', e.target.value)}
                className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden text-emerald-700 dark:text-emerald-300"
              />
              <span className="text-slate-500 text-[9px] print:text-[7px]">Days</span>
            </div>

            <div className="flex items-center gap-1">
              <span className="text-slate-600 dark:text-slate-400 font-medium">Rel. Cont:</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="0"
                value={formData.miscRelativeContact || ''}
                onChange={(e) => handleChange('miscRelativeContact', e.target.value)}
                className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
              />
            </div>

            <div className="flex items-center gap-1">
              <span className="text-slate-600 dark:text-slate-400 font-medium">Grp Dawah:</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="0"
                value={formData.miscGroupDawahTimes || ''}
                onChange={(e) => handleChange('miscGroupDawahTimes', e.target.value)}
                className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
              />
            </div>

            <div className="flex items-center gap-1">
              <span className="text-slate-600 dark:text-slate-400 font-medium">Msg/Email:</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="0"
                value={formData.miscDawahMessagesSent || ''}
                onChange={(e) => handleChange('miscDawahMessagesSent', e.target.value)}
                className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
              />
            </div>
          </div>

          {/* Skill Development Sub-row */}
          <div className="pt-2 print:pt-0.5 mt-2 print:mt-0.5 border-t border-slate-200 dark:border-slate-800 grid grid-cols-1 sm:grid-cols-3 print:grid-cols-3 gap-2 print:gap-1">
            <div className="flex items-center gap-1">
              <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Computer:</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="0"
                value={formData.miscComputerLearningDays || ''}
                onChange={(e) => handleChange('miscComputerLearningDays', e.target.value)}
                className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
              />
              <span className="text-slate-500 text-[9px] print:text-[7px]">Days</span>
            </div>

            <div className="flex items-center gap-1">
              <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Language:</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="0"
                value={formData.miscLanguageLearningDays || ''}
                onChange={(e) => handleChange('miscLanguageLearningDays', e.target.value)}
                className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
              />
              <span className="text-slate-500 text-[9px] print:text-[7px]">Days</span>
            </div>

            <div className="flex items-center gap-1">
              <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Other:</span>
              <input
                type="text"
                placeholder="Skill details..."
                value={formData.miscSkillOther || ''}
                onChange={(e) => handleChange('miscSkillOther', e.target.value)}
                className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-medium focus:outline-hidden"
              />
            </div>
          </div>

          {/* Proactive Report Presentation */}
          <div className="pt-2 print:pt-0.5 mt-2 print:mt-0.5 border-t border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center gap-2 print:flex-row">
            <span className="text-slate-700 dark:text-slate-300 font-medium whitespace-nowrap">
              Report Shown on Own Initiative:
            </span>
            <input
              type="text"
              placeholder="e.g. 1st, 10th, 25th"
              value={formData.miscReportShownDates || ''}
              onChange={(e) => handleChange('miscReportShownDates', e.target.value)}
              className="flex-1 max-w-sm px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-medium focus:outline-hidden"
            />
          </div>
        </div>

        {/* SECTION 6: Supervisor Guidance & Submission Date */}
        <div className="mt-3 print:mt-1 grid grid-cols-1 md:grid-cols-3 print:grid-cols-3 gap-4 print:gap-2 pt-1 print:pt-0">
          {/* Advice box */}
          <div className="md:col-span-2 print:col-span-2 space-y-1 print:space-y-0.5">
            <label className="font-bold text-slate-800 dark:text-slate-200 text-[10.5px] print:text-[8.5px] flex items-center gap-1">
              <span>Supervisor's Guidance / Remarks:</span>
            </label>
            <textarea
              rows={2}
              placeholder="Supervisor remarks and guidance for this month..."
              value={formData.supervisorAdvice || ''}
              onChange={(e) => handleChange('supervisorAdvice', e.target.value)}
              className="w-full p-2 print:p-1 bg-slate-50/70 dark:bg-slate-900/50 border border-slate-300 dark:border-slate-700 rounded-xl text-xs print:text-[8px] print:h-8 focus:outline-hidden font-sans"
            />
          </div>

          {/* Submission and Save box */}
          <div className="flex flex-col justify-between p-3 print:p-1 bg-slate-50/60 dark:bg-slate-900/40 border border-slate-300 dark:border-slate-700 rounded-xl">
            <div className="space-y-1 print:space-y-0.5">
              <div className="text-[10px] print:text-[7.5px] font-bold text-slate-700 dark:text-slate-300">
                Submission Date:
              </div>
              <input
                type="date"
                value={formData.submissionDate || getLocalDateString()}
                onChange={(e) => handleChange('submissionDate', e.target.value)}
                className="w-full px-1 py-0.5 print:py-0 bg-transparent border-b border-slate-400 font-bold focus:outline-hidden text-xs print:text-[8px]"
              />
              <div className="h-6 print:h-4 border-b border-dashed border-slate-400 flex items-end justify-center pb-0.5">
                <span className="text-[9px] print:text-[7px] italic text-slate-400">Verified & Approved</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
